import { Injectable } from "@angular/core";
import { AuditLogInput } from "@app/audit-log/models/audit-log-type.model";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { EquatorDevicesService } from "@app/shared/services/equator-api/equator-devices";
import { EquatorDevicesSecureService } from "@app/shared/services/equator-api/equator-devices-secure";
import { EquatorServicesService } from "@app/shared/services/equator-api/equator-services";
import { map } from "rxjs/operators";
import { CHDevice, DeviceClassObject, DeviceClassObjectProperty, SecurityFobInfo, SecurityZoneInfo } from "../model/ch-device";

@Injectable({
    providedIn: "root",
})
export class CHDeviceService {
    constructor(
        private equatorDeviceService: EquatorDevicesService,
        private equatorDeviceSecureService: EquatorDevicesSecureService,
        private readonly equatorService: EquatorServicesService
    ) {}

    public getAll(uuid: string) {
        return this.equatorDeviceService.getAll(uuid);
    }

    public getClareSummaryInfo(uuid: string) {
        return this.equatorDeviceService.getClareSummaryInfo(uuid);
    }

    public updateDeviceName(uuid: string, deviceUid: string, deviceName: string) {
        return this.equatorDeviceService.setDeviceName(uuid, deviceUid, deviceName);
    }

    public deleteDevice(uuid: string, device: CHDevice, logAction?: AuditLogInput) {
        return this.equatorDeviceService.remove(uuid, device.UID, logAction).pipe(map(_ => ({ uuid, result: device })));
    }

    public deleteAllDevicesForDeviceType(
        uuid: string,
        driverName: string,
        adapterName: string,
        addMethod: string,
        logAction?: AuditLogInput
    ) {
        if (addMethod === "oauth") {
            return this.equatorService.removeOAuthDevice(uuid, adapterName, logAction);
        }
        return this.equatorService.updateAutoDiscovery(
            uuid,
            {
                driverName,
                discoveryEnabled: false,
                removeHdmDevicesForAdapter: adapterName,
            },
            logAction
        );
    }

    public deleteSensor(uuid: string, device: SecurityZoneInfo | SecurityFobInfo) {
        return this.equatorDeviceService
            .invokeDCOOperation(uuid, device.uid, DEVICE_CLASSES.INTRUSION_ZONE_CONFIG, "removeZone")
            .pipe(map(_ => ({ uuid, result: device })));
    }

    public objectifyDCOs(dco: DeviceClassObject): any {
        const deviceClassObj = {};

        if (dco) {
            const propertiesObj: { [key: string]: DeviceClassObjectProperty } = {};
            const properties = dco.Properties;
            if (properties) {
                properties.forEach(property => {
                    propertiesObj[property.Name] = property;
                });
            }
            deviceClassObj[dco.DeviceClass] = {
                DeviceClass: dco.DeviceClass,
                HomeDeviceUID: dco.HomeDeviceUID,
                Properties: propertiesObj,
                Operations: dco.Operations,
            };
        }

        return deviceClassObj;
    }

    public getDeviceClassObjects(uuid: string, deviceUid: string, dco: string) {
        return this.equatorDeviceService.getDCO(uuid, deviceUid, dco);
    }

    public getDeviceClassObjectsForDevices(uuid: string, deviceUids: string[], dco: string, includeMetadata?: boolean) {
        return this.equatorDeviceService.getDeviceClassesForMultipleDevices(uuid, deviceUids, dco, includeMetadata).pipe(
            map(result => {
                const dcos = result.map(res => this.objectifyDCOs(res)[res.DeviceClass]);
                return { uuid, deviceClassObjects: dcos };
            })
        );
    }

    public updateDeviceClassObjectProperty(uuid: string, deviceUid: string, deviceClass: string, property: string, args: string) {
        return this.equatorDeviceService.setDCOProperty(uuid, deviceUid, deviceClass, property, args);
    }

    public invokeDeviceClassObjectOperation(
        uuid: string,
        deviceUid: string,
        deviceClass: string,
        operation: string,
        args?: { [key: string]: string | number | boolean }
    ) {
        return this.equatorDeviceService.invokeDCOOperation(uuid, deviceUid, deviceClass, operation, args);
    }

    public updateSecureDeviceClassObjectOperation(
        uuid: string,
        deviceUid: string,
        deviceClass: string,
        property: string,
        args?: { [key: string]: string | number }
    ) {
        return this.equatorDeviceSecureService.invokeDCOOperation(uuid, deviceUid, deviceClass, property, args);
    }
}
